import { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import { useScope } from "hooks/useScope";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdAssessment } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useCreateOrganizationAssessmentMutation } from "reduxFiles/api/assessmentApi";
import {
  useDeleteCompanyMutation,
  useGetCompaniesQuery,
  useGetIndustriesQuery,
  useGetProvincesQuery,
  useLazyGetUserOrganizationsQuery,
} from "reduxFiles/api/companyApi";
import { useAppSelector } from "reduxFiles/hooks";
import { enumToOptions } from "services/helper";
import { GroupType } from "types/assessmentTypes";
import {
  ActivityType,
  Company,
  CompanyStage,
  CompanyType,
} from "types/companyTypes";
import { Column, TableAction } from "types/globalTypes";
import { UserProfile } from "types/userTypes";
import CompanyCards from "views/company/companies/components/CompanyCards";

const Companies = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const scope = useScope();
  const user: UserProfile = useAppSelector((state) => state.auth.user);

  const { data: provinces } = useGetProvincesQuery();
  const { data: industries } = useGetIndustriesQuery();

  // search organizations
  const [triggerOrganizations] = useLazyGetUserOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // industrials
  const [triggerIndustrials] = useLazyGetUserOrganizationsQuery();
  const searchIndustrials = async (text: string) => {
    try {
      const res = await triggerIndustrials({
        page: 1,
        limit: 50,
        filters: { name: text, industrial: true },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // states
  const [item, setItem] = useState<Company | null>(null);
  const confirmModal = useModal();
  const confirmOrgAssessmentModal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetCompaniesQuery({
    scope,
    page: scope === "user" ? 1 : pagination.page,
    limit: scope === "user" ? 50 : pagination.limit,
    filters,
  });

  const [deleteApi, { isLoading: isDeleting }] = useDeleteCompanyMutation();

  const [createOrgAssessmentApi, { isLoading: isCreating }] =
    useCreateOrganizationAssessmentMutation();

  // form
  const fields: Field[] = [
    {
      name: "name",
      label: t("company.fields.name"),
      type: "text",
      filtered: true,
    },
    {
      name: "fullname",
      label: t("user.fullname"),
      type: "text",
      filtered: scope === "admin",
    },
    {
      name: "nationalId",
      label: t("company.fields.nationalId"),
      type: "text",
      filtered: true,
    },
    {
      name: "organizationId",
      label: t("organization.name"),
      type: "select",
      onSearch: searchOrganizations,
      filtered: scope === "admin",
    },
    {
      name: "industrialId",
      label: t("organization.industrial"),
      type: "select",
      onSearch: searchIndustrials,
      filtered: scope === "admin",
    },
    {
      name: "provinceId",
      label: t("company.fields.province"),
      type: "select",
      options:
        scope === "organization" && user.organization?.provinces?.length > 0
          ? user.organization?.provinces?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            }))
          : provinces?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            })),
      filtered: scope !== "user",
    },
    {
      name: "industrySectorId",
      label: t("company.fields.industrySectorId"),
      type: "select",
      options:
        scope === "organization" && user.organization?.industrials?.length > 0
          ? user.organization?.industrials?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            }))
          : industries?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            })),
      filtered: scope !== "user",
    },
    {
      name: "companyType",
      label: t("company.fields.companyType"),
      type: "select",
      options: enumToOptions(CompanyType, t, "company.companyTypes"),
      filtered: scope !== "user",
    },
    {
      name: "activityType",
      label: t("company.fields.activityType"),
      type: "select",

      options: enumToOptions(ActivityType, t, "company.activityTypes"),
      filtered: scope !== "user",
    },
    {
      name: "companyStage",
      label: t("company.fields.companyStage"),
      type: "select",
      options: enumToOptions(CompanyStage, t, "company.companyStages"),
      filtered: scope !== "user",
    },
    {
      name: "companySize",
      label: t("company.fields.companySize"),
      type: "select",
      options: enumToOptions(GroupType, t, "group.types"),
      filtered: scope !== "user",
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<Company>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "name",
      title: t("company.fields.name"),
    },
    {
      key: "nationalId",
      title: t("company.fields.nationalId"),
    },
    {
      key: "totalStaffCount",
      title: t("company.fields.companySize"),
      render: (row) => t(`group.types.${row.totalStaffCount?.companySize}`),
      // options: enumToOptions(GroupType, t, "group.types"),
    },
    {
      key: "user",
      title: t("user.fullname"),
      render: (row) => row.user?.fullname || "-",
    },
    {
      key: "organization",
      title: t("organization.name"),
      render: (row) =>
        row.organization
          ? row.organization?.name + " " + row.organization?.province?.name
          : "-",
    },
  ];

  const actions: TableAction<Company>[] = [
    {
      label: t("company.createOrganizationAssessment"),
      onClick: (row) => {
        setItem(row);
        confirmOrgAssessmentModal.openModal();
      },
      color: "secondary",
      icon: <MdAssessment />,
      hidden: scope !== "organization" || !user?.organization?.form,
    },
    {
      label: t("company.viewCompany"),
      onClick: (row) => {
        navigate(`/${scope}/companies/${row.id}/view`);
      },
      color: "primary",
      see: true,
    },
    {
      label: t("company.editCompany"),
      onClick: (row) => {
        navigate(`/company/companies/${row.id}/edit`);
      },
      color: "secondary",
      edit: true,
      hidden: scope !== "user",
    },
    {
      label: t("company.deleteCompany"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
      hidden: scope === "organization",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleDelete = async () => {
    try {
      if (!item) return;

      await deleteApi({
        id: item.id,
        scope,
      }).unwrap();

      confirmModal.closeModal();

      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateOrgAssessment = async () => {
    try {
      if (!item) return;

      await createOrgAssessmentApi({
        companyId: item.id,
      }).unwrap();

      confirmOrgAssessmentModal.closeModal();

      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("company.companyList")}
          createBtn={
            scope === "user"
              ? {
                  title: t("company.addCompany"),
                  handler: () => {
                    navigate("/company/companies/create");
                  },
                }
              : undefined
          }
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        {scope !== "user" ? (
          <>
            <DynamicTable<Company>
              columns={columns}
              data={data?.items ?? []}
              actions={actions}
              loading={isFetching}
              pagination={pagination}
            />
            <Pagination
              currentPage={pagination?.page || 1}
              totalPages={data?.pageCount || 1}
              onPageChange={(newPage) => {
                setPagination((prev) => ({ ...prev, page: newPage }));
              }}
              totalItems={data?.total || 0}
              currentLimit={pagination.limit}
              onLimitChange={handleLimitChange}
            />
          </>
        ) : (
          <CompanyCards
            items={data?.items ?? []}
            loading={isFetching}
            onEdit={(row) => {
              navigate(`/company/companies/${row.id}/edit`);
            }}
            onDelete={(row) => {
              setItem(row);
              confirmModal.openModal();
            }}
          />
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("company.deleteCompany")}
          desc={t("actions.confirmDeleteDesc", { name: item?.name || "" })}
        />

        <ConfirmModal
          isOpen={confirmOrgAssessmentModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmOrgAssessmentModal.closeModal();
          }}
          handler={handleCreateOrgAssessment}
          loading={isCreating}
          primary
          title={t("company.createOrganizationAssessment")}
          desc={t("company.createOrganizationAssessmentDesc", {
            name: item?.name || "",
          })}
        />
      </div>
    </div>
  );
};

export default Companies;
